/**
 * CFB game-event props — classification by the WIRE, not the ticker.
 *
 * hydracore cfb-game-props (shipped `main @ eb77800`, 2026-08-30) stamps the
 * Kalshi SERIES CLASS on every Kalshi row as `class` (edges forward it); the
 * CFB game series beyond moneyline/spread/total/period lines classify as: the 1H/FT double result, the
 * overtime and 2-point yes/nos, the first-to-score / first-TD-team 3-ways,
 * the highest-scoring quarter, the per-team stat O/U ladders (with a `stat`
 * discriminator), the game-total touchdown/field-goal ladders, and the D/ST
 * touchdown "player" prop. A consumer renders these by
 * `class` + `period` + `subtitle` (+ `canonicalYesTeam` for the sided ones);
 * `marketType` stays the coarse `game | future` discriminator. It must NOT
 * re-parse the ticker: CFB codes are variable-width 2–5 letters and the
 * double-result suffix is a packed combo (`GTGT`, `COLOGT`, `TIEGT`) that
 * `lib/kalshiTicker` would split wrongly.
 *
 * Contract rules honoured here (HYDRA_API.md "CFB game-event props"):
 *  - unknown or absent `class` → null (rule 8: ignore, never guess a family);
 *  - a sided row (team stat / team total / the D/ST touchdown player-prop)
 *    without an honest side (`canonicalYesTeam` inside the row's canonical
 *    pair) → null — an unlabelled ladder is worse than none (DNL-002);
 *  - an O/U row without `floorStrike` → null (no line to trade);
 *  - a `team-stat` whose `stat` is outside the documented vocabulary → null
 *    (DNL-004 — additive contract: ignore, never render a ladder we cannot
 *    name);
 *  - the label is the venue's own wording (`subtitle`, Kalshi
 *    `yes_sub_title`) — the ONLY source that can spell "Georgia Tech wins 1H /
 *    Colorado wins game" or "1+ overtime periods" correctly. No subtitle →
 *    null; `title` is the SERIES title and would make every outcome of an
 *    enum read identically (DNL-003).
 *
 * Kalshi-only by construction: Polymarket lists no CFB counterpart for any of
 * these families (brief §5), so nothing here ever pairs.
 */

import type { HydraMarket, CfbGameEventClass } from '@/clients/HydraClient';

/** Enum families — one YES market per outcome, rendered as one grouped pane. */
export type CfbEnumClass = 'half-fulltime' | 'first-td-team' | 'first-to-score' | 'highest-quarter';
/** Single yes/no families. */
export type CfbBinaryClass = 'overtime' | 'two-point' | 'every-quarter-btts' | 'player-prop';
/** Game-total O/U ladders (names shared with the OP full-feed-B families). */
export type CfbTotalClass = 'total-touchdowns' | 'total-fieldgoals';

export const CFB_ENUM_CLASSES: ReadonlySet<string> = new Set<CfbEnumClass>([
  'half-fulltime', 'first-td-team', 'first-to-score', 'highest-quarter',
]);
export const CFB_BINARY_CLASSES: ReadonlySet<string> = new Set<CfbBinaryClass>([
  'overtime', 'two-point', 'every-quarter-btts', 'player-prop',
]);
export const CFB_TOTAL_CLASSES: ReadonlySet<string> = new Set<CfbTotalClass>([
  'total-touchdowns', 'total-fieldgoals',
]);

/** Pane titles per enum class, in render order. */
export const CFB_ENUM_GROUPS: ReadonlyArray<{ cls: CfbEnumClass; label: string }> = [
  { cls: 'first-to-score', label: 'First Team to Score' },
  { cls: 'first-td-team', label: 'First Team to Score a Touchdown' },
  { cls: 'half-fulltime', label: '1st Half / Full Time Result' },
  { cls: 'highest-quarter', label: 'Highest-Scoring Quarter' },
];

export const CFB_BINARY_LABELS: Readonly<Record<CfbBinaryClass, string>> = {
  overtime: 'Overtime',
  'two-point': '2-Point Conversion',
  'every-quarter-btts': 'Both Teams Score Every Quarter',
  'player-prop': 'D/ST Touchdown',
};

export const CFB_TOTAL_LABELS: Readonly<Record<CfbTotalClass, string>> = {
  'total-touchdowns': 'Total Touchdowns',
  'total-fieldgoals': 'Total Field Goals',
};

/** `team-stat` discriminator → pane label. `pts` is the plain team total
 *  (class `team-total`, KXNCAAFTEAMTOTAL), folded into the same sided pane.
 *  Order = render order. */
export const CFB_TEAM_STAT_LABELS: ReadonlyArray<{ stat: string; label: string }> = [
  { stat: 'pts', label: 'Points' },
  { stat: 'yds', label: 'Total Yards' },
  { stat: 'td', label: 'Touchdowns' },
  { stat: 'fg', label: 'Field Goals' },
  { stat: 'rush-yds', label: 'Rushing Yards' },
  { stat: 'rush-att', label: 'Rushing Attempts' },
  { stat: 'rush-td', label: 'Rushing Touchdowns' },
  { stat: 'rec', label: 'Receptions' },
  { stat: 'rec-yds', label: 'Receiving Yards' },
  { stat: 'rec-td', label: 'Receiving Touchdowns' },
  { stat: 'int', label: 'Interceptions' },
  { stat: 'sack', label: 'Sacks' },
  { stat: 'to', label: 'Turnovers Forced' },
];

/** The documented `stat` vocabulary (HYDRA_API.md "CFB periods and game-event
 *  props"); `pts` is g1's own token for the `team-total` class and is NOT a
 *  wire value. */
const CFB_WIRE_STATS: ReadonlySet<string> = new Set(
  CFB_TEAM_STAT_LABELS.map((s) => s.stat).filter((s) => s !== 'pts'),
);

export function cfbTeamStatLabel(stat: string): string {
  return CFB_TEAM_STAT_LABELS.find((s) => s.stat === stat)?.label ?? stat;
}

export function cfbTeamStatRank(stat: string): number {
  const i = CFB_TEAM_STAT_LABELS.findIndex((s) => s.stat === stat);
  return i === -1 ? CFB_TEAM_STAT_LABELS.length : i;
}

export type CfbWireMarket =
  | {
      kind: 'enum';
      cls: CfbEnumClass;
      label: string;
      /** Canonical code of the team the YES outcome names (first-to-score /
       *  first-TD team rows), when core stamped one inside the row's pair.
       *  Null on NONE/tie/combo rows and whenever the stamp is absent — the
       *  row still renders (by its own label); it just isn't team-sided. */
      teamCode: string | null;
    }
  | {
      kind: 'binary';
      cls: CfbBinaryClass;
      label: string;
      /** `player-prop` (D/ST touchdown) is team-sided: the team whose unit
       *  scores. Null on the unsided yes/nos (overtime, two-point, every-
       *  quarter BTTS). */
      teamCode: string | null;
    }
  | {
      kind: 'team-stat';
      /** `pts` for the plain team total; the wire `stat` otherwise. */
      stat: string;
      teamCode: string;
      /** The O/U line (`floorStrike`); YES = over. */
      line: number;
    }
  | { kind: 'total'; cls: CfbTotalClass; line: number };

const isCfbSport = (m: HydraMarket): boolean => {
  const s = m.sport?.toUpperCase();
  if (s === 'CFB' || s === 'NCAAF') return true;
  const key = m.canonicalGameKeyV2 ?? m.canonicalGameKey ?? '';
  return key.startsWith('ncaaf|');
};

/** Venue wording (`subtitle` = Kalshi `yes_sub_title`), or nothing — never
 *  invented and never the series `title` (DNL-003). */
const rowLabel = (m: HydraMarket): string | null => m.subtitle?.trim() || null;

/** `canonicalYesTeam` only counts when it names one end of the row's own
 *  canonical pair — the same fail-closed rule the MLB team-total pass uses. */
const sidedTeam = (m: HydraMarket): string | null => {
  const yes = m.canonicalYesTeam?.toUpperCase();
  if (!yes) return null;
  const away = m.canonicalAwayCode?.toUpperCase();
  const home = m.canonicalHomeCode?.toUpperCase();
  return yes === away || yes === home ? yes : null;
};

/**
 * Classify one market frame into a CFB game-event family, or null when it is
 * not one (any other sport / venue / class, a non-full period, or a row the
 * contract lets us render only dishonestly).
 */
export function classifyCfbWireMarket(m: HydraMarket): CfbWireMarket | null {
  if (m.source !== 'kalshi') return null;
  if (!isCfbSport(m)) return null;
  // The ROW's class — never `marketType` (game|future) and never the ticker.
  const cls: CfbGameEventClass | string | undefined = m.class;
  if (!cls) return null;
  // These are full-game families. A period-scoped variant would be a new
  // contract, not a rendering of this one — ignore it (rule 8).
  if ((m.period ?? 'full') !== 'full') return null;

  if (CFB_ENUM_CLASSES.has(cls)) {
    const label = rowLabel(m);
    if (!label) return null;
    return { kind: 'enum', cls: cls as CfbEnumClass, label, teamCode: sidedTeam(m) };
  }
  if (CFB_BINARY_CLASSES.has(cls)) {
    const label = rowLabel(m);
    if (!label) return null;
    // The D/ST touchdown prop names a team — require the stamp (DNL-002).
    const teamCode = cls === 'player-prop' ? sidedTeam(m) : null;
    if (cls === 'player-prop' && !teamCode) return null;
    return { kind: 'binary', cls: cls as CfbBinaryClass, label, teamCode };
  }
  if (cls === 'team-stat' || cls === 'team-total') {
    const teamCode = sidedTeam(m);
    if (!teamCode) return null;
    if (typeof m.floorStrike !== 'number' || !Number.isFinite(m.floorStrike)) return null;
    const stat = cls === 'team-total' ? 'pts' : m.stat?.trim().toLowerCase();
    if (!stat) return null;
    if (cls === 'team-stat' && !CFB_WIRE_STATS.has(stat)) return null;
    return { kind: 'team-stat', stat, teamCode, line: m.floorStrike };
  }
  if (CFB_TOTAL_CLASSES.has(cls)) {
    if (typeof m.floorStrike !== 'number' || !Number.isFinite(m.floorStrike)) return null;
    return { kind: 'total', cls: cls as CfbTotalClass, line: m.floorStrike };
  }
  return null;
}
